import { useFormsTranslation } from '@/hooks/useTranslation';
import { FileText, Shield } from 'lucide-react';
import { useCallback, useEffect, useMemo } from 'react';
import { TabMetadata } from '../types/kycFormTabs.types';

interface UseKycFormNavigationOptions {
  requiredFormIds: string[];
  activeTab: string;
  setActiveTab: (tab: string) => void;
}

/**
 * Custom hook for managing KYC form tab navigation.
 * Handles active tab state, tab filtering, and navigation logic.
 */
export function useKycFormNavigation({
  requiredFormIds,
  activeTab,
  setActiveTab,
}: UseKycFormNavigationOptions) {
  const tForms = useFormsTranslation();

  /**
   * Define all available tabs with their metadata.
   * Memoized to prevent recreating on every render.
   */
  const allTabs = useMemo<TabMetadata[]>(
    () => [
      {
        id: 'identification',
        label: tForms.t('identification.title'),
        description: 'Identity verification',
        icon: Shield,
      },
      {
        id: 'form-k',
        label: 'Form K',
        description: 'Form K requirements',
        icon: FileText,
      },
      {
        id: 'form-a',
        label: 'Form A',
        description: 'Form A requirements',
        icon: FileText,
      },
    ],
    [tForms]
  );

  /**
   * Filter tabs to show only those required based on form data.
   */
  const tabs = useMemo(
    () => allTabs.filter(tab => requiredFormIds.includes(tab.id)),
    [allTabs, requiredFormIds]
  );

  /**
   * Effect to handle active tab when required forms change.
   * If the active tab is no longer required, switch to identification.
   */
  useEffect(() => {
    if (!requiredFormIds.includes(activeTab)) {
      setActiveTab('identification');
    }
  }, [requiredFormIds, activeTab, setActiveTab]);

  /**
   * Get current tab index in the filtered tabs array.
   */
  const getCurrentTabIndex = useCallback(
    () => tabs.findIndex(tab => tab.id === activeTab),
    [tabs, activeTab]
  );

  /**
   * Check if user can navigate to a specific tab.
   * Currently allows navigation to any required tab.
   */
  const canNavigateToTab = useCallback(
    (tabId: string) => requiredFormIds.includes(tabId),
    [requiredFormIds]
  );

  /**
   * Navigate to the previous tab.
   */
  const goToPreviousTab = useCallback(() => {
    const currentIndex = getCurrentTabIndex();
    if (currentIndex > 0) {
      setActiveTab(tabs[currentIndex - 1].id);
    }
  }, [getCurrentTabIndex, tabs, setActiveTab]);

  /**
   * Navigate to the next tab.
   */
  const goToNextTab = useCallback(() => {
    const currentIndex = getCurrentTabIndex();
    if (currentIndex < tabs.length - 1) {
      setActiveTab(tabs[currentIndex + 1].id);
    }
  }, [getCurrentTabIndex, tabs, setActiveTab]);

  return {
    activeTab,
    setActiveTab,
    tabs,
    allTabs,
    getCurrentTabIndex,
    canNavigateToTab,
    goToPreviousTab,
    goToNextTab,
  };
}
